import { decodeString } from "@findemdev/findem-helpers";
import { useMemo } from "react";
import { useGetCompanyLogosByMetaRefsQuery } from "../../../services/pub";
import { useGetPeopleListIcpsQuery } from "../../../services/searches";
import type { IExecProjectListItem } from "../../../types/agent-pipelines";
import type { IOrgV2 } from "../../../types/orgs";
import type { IGetCompanyLogosResponseDataObj } from "../../../types/pub";
import type {
  IPeopleListIcpsResponse,
  IPeopleListSearch,
} from "../../../types/searchRequest";
import type { IFetchAllUsersResponse } from "../../../types/users";
import {
  getRaasProjectListStatusView,
  RaasStatusAudience,
} from "../status";
import { getRaasUsersById } from "../identity";
import type { IProjectRow } from "../types";
import {
  formatDateTitle,
  formatRelativeTime,
  getPrimaryCompanyName,
} from "../utils";
import { useVisibleProjectEnrichment } from "./useVisibleProjectEnrichment";

interface IUseRaasClientPortalRowsArgs {
  allOrgs?: IOrgV2[];
  allUsers?: IFetchAllUsersResponse;
  includeClientDetails: boolean;
  enrichmentScopeKey?: string;
  joid?: string;
  projects?: IExecProjectListItem[];
  visibleProjectRefs?: string[];
  viewerCompanyName?: string;
}

const getPeopleListIcpIds = ({
  projects = [],
}: {
  projects?: IExecProjectListItem[];
}) => {
  if (projects.length === 0) return [];

  const peopleListIcpIds = new Set<string>();

  for (const project of projects) {
    const peopleListIcpId = project.people_list_icp_id?.trim();
    if (peopleListIcpId) peopleListIcpIds.add(peopleListIcpId);
  }

  return Array.from(peopleListIcpIds);
};

const getPeopleListPipelineCount = ({
  search,
}: {
  search?: IPeopleListSearch;
}) => {
  const matchFeedback = search?.cxt_data?.match_feedback;
  if (!matchFeedback?.length) return 0;

  const shortlistedProfileIds = new Set<string>();

  for (const feedback of matchFeedback) {
    if (feedback.shortlisted === true && !feedback.archived && feedback.prid) {
      shortlistedProfileIds.add(feedback.prid);
    }
  }

  return shortlistedProfileIds.size;
};

const getPeopleListPipelineCountsByIcpId = ({
  peopleListIcps,
}: {
  peopleListIcps?: IPeopleListIcpsResponse;
}) => {
  if (!peopleListIcps) return {};

  const countsByIcpId: Record<string, number> = {};

  for (const [icpId, search] of Object.entries(peopleListIcps)) {
    countsByIcpId[icpId] = getPeopleListPipelineCount({ search });
  }

  return countsByIcpId;
};

export const useRaasClientPortalRows = ({
  allOrgs,
  allUsers,
  enrichmentScopeKey,
  includeClientDetails,
  joid,
  projects,
  visibleProjectRefs,
  viewerCompanyName,
}: IUseRaasClientPortalRowsArgs) => {
  const usersById = useMemo(
    () => getRaasUsersById({ allOrgs, allUsers }),
    [allOrgs, allUsers],
  );
  const supplementalProjects = useMemo(() => {
    if (visibleProjectRefs === undefined) return projects || [];

    const visibleProjectRefSet = new Set(visibleProjectRefs);
    return (projects || []).filter((project) =>
      visibleProjectRefSet.has(project.ref),
    );
  }, [projects, visibleProjectRefs]);
  const visibleProjectRefSet = useMemo(
    () => new Set(visibleProjectRefs || []),
    [visibleProjectRefs],
  );
  const usesVisibleProjectEnrichment = visibleProjectRefs !== undefined;
  const peopleListIcpIds = useMemo(
    () => getPeopleListIcpIds({ projects: supplementalProjects }),
    [supplementalProjects],
  );

  const {
    data: peopleListIcps,
    isError: isPeopleListIcpsError,
    isFetching: isPeopleListIcpsFetching,
    isLoading: isPeopleListIcpsLoading,
    isSuccess: isPeopleListIcpsSuccess,
  } = useGetPeopleListIcpsQuery(
    {
      icpIds: peopleListIcpIds,
      joid: joid || "",
    },
    {
      skip:
        usesVisibleProjectEnrichment ||
        !joid ||
        peopleListIcpIds.length === 0,
    }
  );
  const visibleProjectEnrichment = useVisibleProjectEnrichment({
    enabled: usesVisibleProjectEnrichment,
    includeClientDetails,
    joid,
    projects: supplementalProjects,
    scopeKey: enrichmentScopeKey,
  });
  const effectivePeopleListIcps = usesVisibleProjectEnrichment
    ? visibleProjectEnrichment.peopleListIcps
    : peopleListIcps;
  const peopleListPipelineCounts = useMemo(
    () => getPeopleListPipelineCountsByIcpId({
      peopleListIcps: effectivePeopleListIcps,
    }),
    [effectivePeopleListIcps],
  );
  const hasPeopleListIcpIds = peopleListIcpIds.length > 0;
  const isPeopleListIcpsPending =
    hasPeopleListIcpIds &&
    (
      isPeopleListIcpsFetching ||
      isPeopleListIcpsLoading ||
      (!isPeopleListIcpsSuccess && !isPeopleListIcpsError)
    );

  const rows = useMemo<IProjectRow[]>(() => {
    const statusAudience = includeClientDetails
      ? RaasStatusAudience.Internal
      : RaasStatusAudience.Client;

    return (projects || []).map((project) => {
      const hiringForLabel = getPrimaryCompanyName(project.hiring_company_name || project.client_name || "-");
      const clientLabel = includeClientDetails
        ? getPrimaryCompanyName(project.client_name || "-")
        : (viewerCompanyName || "-");
      const clientFilterValue = includeClientDetails
        ? (project.client_company_id || undefined)
        : (clientLabel !== "-" ? clientLabel : undefined);
      const updatedAtMs = project.updated_at ? new Date(project.updated_at).getTime() : undefined;
      const peopleListIcpId = project.people_list_icp_id?.trim();
      // RAAS projects only store the People List ICP id. The Pipeline column is
      // the active shortlisted profile count from that ICP's match feedback.
      const peopleListPipelineCount = peopleListIcpId
        ? peopleListPipelineCounts[peopleListIcpId]
        : undefined;
      const isPipelineLoading = !usesVisibleProjectEnrichment
        ? Boolean(isPeopleListIcpsPending)
        : Boolean(
            visibleProjectRefSet.has(project.ref) &&
            peopleListIcpId &&
            !visibleProjectEnrichment.settledPeopleListIcpIds.has(
              peopleListIcpId,
            ),
          );
      const pipelineCount =
        peopleListPipelineCount ?? project.pipeline_count;
      const pipelineLabel = pipelineCount > 0 ? String(pipelineCount) : "—";
      const actingUser = project.acting_user_id
        ? usersById.get(project.acting_user_id)
        : undefined;
      const actingUserName = decodeString(actingUser?.name || "");
      const actingUserLabel =
        actingUserName || project.acting_user_id || "-";

      return {
        project: project as unknown as IProjectRow["project"],
        projectNameLabel: decodeString(project.project_name || ""),
        actingUser,
        actingUserEmailLabel: actingUser?.email,
        actingUserLabel,
        clientFilterValue,
        clientLabel,
        clientCompanyMetaRef: project.client_company_id || undefined,
        hiringForLabel,
        hiringForCompanyMetaRef: project.hiring_company_id || undefined,
        isPipelineLoading,
        milestoneLabel: decodeString(project.milestone || "-"),
        pipelineLabel,
        status: getRaasProjectListStatusView({
          audience: statusAudience,
          value: project.status,
        }),
        updatedAtLabel: formatRelativeTime({ timestamp: updatedAtMs }),
        updatedAtTitle: formatDateTitle({ timestamp: updatedAtMs }),
      };
    });
  }, [
    includeClientDetails,
    isPeopleListIcpsPending,
    peopleListPipelineCounts,
    projects,
    usersById,
    usesVisibleProjectEnrichment,
    visibleProjectEnrichment.settledPeopleListIcpIds,
    visibleProjectRefSet,
    viewerCompanyName,
  ]);

  const companyMetaRefs = useMemo(() => {
    const metaRefs = new Set<string>();

    supplementalProjects.forEach((project) => {
      if (includeClientDetails && project.client_company_id) {
        metaRefs.add(project.client_company_id);
      }
      if (project.hiring_company_id) metaRefs.add(project.hiring_company_id);
    });

    return Array.from(metaRefs);
  }, [includeClientDetails, supplementalProjects]);

  const { data: companyLogoDetails } = useGetCompanyLogosByMetaRefsQuery(
    {
      joid: joid || "",
      metaRefs: companyMetaRefs,
    },
    {
      skip:
        usesVisibleProjectEnrichment ||
        !joid ||
        companyMetaRefs.length === 0,
    },
  );

  const regularCompanyDetailsByMetaRef = useMemo<
    Record<string, IGetCompanyLogosResponseDataObj | undefined>
  >(() => {
    if (!companyLogoDetails?.data) return {};

    return companyLogoDetails.data.reduce(
      (acc, value) => {
        if (value?.company?.meta_ref) {
          acc[value.company.meta_ref] = value;
        }

        return acc;
      },
      {} as Record<string, IGetCompanyLogosResponseDataObj | undefined>,
    );
  }, [companyLogoDetails]);
  const companyDetailsByMetaRef = usesVisibleProjectEnrichment
    ? visibleProjectEnrichment.companyDetailsByMetaRef
    : regularCompanyDetailsByMetaRef;

  return { rows, companyDetailsByMetaRef };
};
